"use client";

import { useState } from "react";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";

type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

type ConfidenceLevel = "90" | "95" | "99";

const Z_BY_CONFIDENCE: Record<ConfidenceLevel, number> = {
  "90": 1.645,
  "95": 1.96,
  "99": 2.576,
};

const TABLE_SAMPLE_SIZES = [100, 500, 1000, 2000];

interface MoeResult {
  z: number;
  p: number;
  n: number;
  population: number | null;
  fpc: number | null;
  moePercent: number;
}

function computeMoe(
  confidence: ConfidenceLevel,
  n: number | null,
  proportionPercent: number | null,
  population: number | null
): MoeResult | null {
  if (n === null || n < 2) {
    return null;
  }
  if (
    proportionPercent === null ||
    proportionPercent <= 0 ||
    proportionPercent >= 100
  ) {
    return null;
  }
  if (population !== null && (Number.isNaN(population) || population <= n)) {
    return null;
  }
  const z = Z_BY_CONFIDENCE[confidence];
  const p = proportionPercent / 100;
  let fpc: number | null = null;
  if (population !== null) {
    fpc = Math.sqrt((population - n) / (population - 1));
  }
  const base = z * Math.sqrt((p * (1 - p)) / n);
  const moe = fpc === null ? base : base * fpc;
  return { z, p, n, population, fpc, moePercent: moe * 100 };
}

function parseOptionalPopulation(text: string): number | null {
  if (text.trim().length === 0) {
    return null;
  }
  // NaN, not null: a non-empty field that fails to parse must still read as
  // "population given but invalid" to computeMoe, distinct from "not given".
  return parseNumberInput(text) ?? Number.NaN;
}

function formatNum(value: number, fmt: NumberFormatter): string {
  return fmt(value, { maximumFractionDigits: 4 });
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const MARGIN_OF_ERROR_CALCULATOR_STRINGS = {
  sampleSizeLabel: "Sample size (n)",
  confidenceLevelLabel: "Confidence level",
  confidence90: "90% (z = 1.645)",
  confidence95: "95% (z = 1.96)",
  confidence99: "99% (z = 2.576)",
  proportionLabel: "Observed proportion (%) — use 50 if unsure",
  populationLabel: "Population size — optional, for the finite correction",
  populationPlaceholder: "e.g. 10000",
  results: "Results",
  marginOfError: "Margin of error",
  zScoreUsed: "Z-score used",
  finiteCorrection: "Finite correction",
  none: "None",
  howThisWasCalculated: "How this was calculated",
  finitePopulationCorrection:
    "Finite population correction: √((N − n) / (N − 1)) = √(({population} − {n}) / ({population} − 1)) = {fpc}, giving MOE = {moe}",
  percentageSummary:
    "As a percentage that is {margin} at {confidence}% confidence. A result of {observed}% in your survey means the true population value is likely between {low}% and {high}%.",
  marginOfErrorAtCommonSampleSizes:
    "Margin of error at common sample sizes ({confidence}% confidence, no finite correction)",
  sampleSize: "Sample size",
  emptyStateMessage:
    "Enter a sample size of at least 2 and a proportion between 0 and 100. If you give a population size, it must be larger than the sample.",
} as const;

export default function MarginOfErrorCalculatorWidget() {
  const [sampleSize, setSampleSize] = useState("");
  const [confidence, setConfidence] = useState<ConfidenceLevel>("95");
  const [proportion, setProportion] = useState("50");
  const [populationText, setPopulationText] = useState("");

  const strings = useToolUi(MARGIN_OF_ERROR_CALCULATOR_STRINGS);
  const fmt = useToolNumber();

  const population = parseOptionalPopulation(populationText);
  const result = computeMoe(
    confidence,
    parseNumberInput(sampleSize),
    parseNumberInput(proportion),
    population
  );

  const tableRows = TABLE_SAMPLE_SIZES.map((n) => ({
    n,
    result: computeMoe(confidence, n, parseNumberInput(proportion), null),
  }));

  return (
    <>
      {/* Inputs */}
      <section className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="moe-n"
          >
            {strings.sampleSizeLabel}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="moe-n"
            inputMode="numeric"
            onChange={(e) => setSampleSize(e.target.value)}
            placeholder="400"
            type="text"
            value={sampleSize}
          />
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="moe-confidence"
          >
            {strings.confidenceLevelLabel}
          </label>
          <select
            className="w-full rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="moe-confidence"
            onChange={(e) => setConfidence(e.target.value as ConfidenceLevel)}
            value={confidence}
          >
            <option value="90">{strings.confidence90}</option>
            <option value="95">{strings.confidence95}</option>
            <option value="99">{strings.confidence99}</option>
          </select>
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="moe-proportion"
          >
            {strings.proportionLabel}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="moe-proportion"
            inputMode="decimal"
            onChange={(e) => setProportion(e.target.value)}
            placeholder="50"
            type="text"
            value={proportion}
          />
        </div>
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="moe-population"
          >
            {strings.populationLabel}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="moe-population"
            inputMode="numeric"
            onChange={(e) => setPopulationText(e.target.value)}
            placeholder={strings.populationPlaceholder}
            type="text"
            value={populationText}
          />
        </div>
      </section>

      {/* Results */}
      {result ? (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.results}
          </h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label={strings.marginOfError}
              value={`±${fmt(result.moePercent, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
            />
            <StatCard
              label={strings.zScoreUsed}
              value={formatNum(result.z, fmt)}
            />
            <StatCard
              label={strings.finiteCorrection}
              value={
                result.fpc === null ? strings.none : formatNum(result.fpc, fmt)
              }
            />
          </div>

          {/* Working */}
          <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
            <h3 className="font-semibold text-foreground text-lg">
              {strings.howThisWasCalculated}
            </h3>
            <p className="font-mono text-muted-foreground text-sm leading-relaxed">
              MOE = z × √(p(1−p) / n) = {formatNum(result.z, fmt)} × √(
              {formatNum(result.p, fmt)} × {formatNum(1 - result.p, fmt)} /{" "}
              {formatNum(result.n, fmt)}) = {formatNum(result.z, fmt)} ×{" "}
              {formatNum(
                Math.sqrt((result.p * (1 - result.p)) / result.n),
                fmt
              )}{" "}
              ={" "}
              {formatNum(
                result.z * Math.sqrt((result.p * (1 - result.p)) / result.n),
                fmt
              )}
            </p>
            {result.fpc !== null && result.population !== null && (
              <p className="font-mono text-muted-foreground text-sm leading-relaxed">
                {t(strings.finitePopulationCorrection, {
                  population: formatNum(result.population, fmt),
                  n: formatNum(result.n, fmt),
                  fpc: formatNum(result.fpc, fmt),
                  moe: formatNum(result.moePercent / 100, fmt),
                })}
              </p>
            )}
            <p className="text-muted-foreground text-sm leading-relaxed">
              {renderSlots(
                t(strings.percentageSummary, {
                  confidence,
                  observed: formatNum(result.p * 100, fmt),
                  low: formatNum(result.p * 100 - result.moePercent, fmt),
                  high: formatNum(result.p * 100 + result.moePercent, fmt),
                  margin: "{margin}",
                }),
                {
                  margin: (
                    <strong className="text-foreground">
                      ±
                      {fmt(result.moePercent, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      %
                    </strong>
                  ),
                }
              )}
            </p>
          </div>

          {/* Table at common sample sizes */}
          <div className="space-y-3">
            <h3 className="font-semibold text-foreground text-lg">
              {t(strings.marginOfErrorAtCommonSampleSizes, { confidence })}
            </h3>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-muted/50 text-foreground">
                  <tr>
                    <th className="px-4 py-3 font-medium">
                      {strings.sampleSize}
                    </th>
                    <th className="px-4 py-3 font-medium">
                      {strings.marginOfError}
                    </th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  {tableRows.map((row) => (
                    <tr className="border-border border-t" key={row.n}>
                      <td className="px-4 py-3">{formatNum(row.n, fmt)}</td>
                      <td className="px-4 py-3">
                        {row.result === null
                          ? "—"
                          : `±${fmt(row.result.moePercent, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : (
        <p className="text-muted-foreground text-sm">
          {strings.emptyStateMessage}
        </p>
      )}
    </>
  );
}
